<script setup lang="ts">
import type { ComponentPublicInstance, HTMLAttributes } from 'vue'
import { computed, nextTick, ref, useSlots, watch } from 'vue'
import type { ButtonVariants } from './variants'
import type { IconNode } from 'lucide'
import type { IconSize } from '@/components/base/icon/'
import { Loader } from 'lucide'
import { Primitive } from 'reka-ui'
import type { PrimitiveProps } from 'reka-ui'
import { SphIcon } from '@/components/base/icon/'
import { buttonVariants } from './variants'
import { cn } from '@/utils/shadcn/lib/utils'
import { useDeferredRef } from '@/composables/deferred-ref'

defineOptions({
    compatConfig: { MODE: 3 },
})

interface Props extends PrimitiveProps {
    variant?: ButtonVariants['variant']
    size?: 'sm' | 'md'
    icon?: IconNode
    iconRight?: IconNode
    circle?: boolean
    class?: HTMLAttributes['class']
    type?: 'button' | 'submit' | 'reset'

    // content
    label?: string
    ariaLabel?: string
    title?: string

    // state
    loading?: boolean
    disabled?: boolean
    active?: boolean

    // ux
    noActiveEffect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    as: 'button',
    variant: 'default',
    size: 'md',
    icon: undefined,
    iconRight: undefined,
    circle: false,
    class: '',
    type: 'button',

    label: '',
    ariaLabel: '',
    title: '',

    loading: false,
    disabled: false,
    active: false,

    noActiveEffect: false,
})

const emit = defineEmits<{
    click: [event: MouseEvent | PointerEvent | KeyboardEvent]
}>()

const slots = useSlots()

const iconType = computed<'loader' | 'icon' | null>(() => {
    if (internalLoading.value) {
        return 'loader'
    }
    if (props.icon) {
        return 'icon'
    }
    return null
})

const iconKey = computed<string>(() => {
    if (internalLoading.value) {
        return 'loader'
    }
    if (props.icon) {
        return JSON.stringify(props.icon)
    }
    return ''
})

const iconOnly = computed<boolean>(() => {
    return !!(props.icon) && !props.label && !slots.default
})

const sizeProcessed = computed<ButtonVariants['size']>(() => {
    if (iconOnly.value) {
        return (`icon-${props.size}`) as ButtonVariants['size']
    }

    return props.size as ButtonVariants['size']
})

const iconSize = computed<IconSize>(() => props.size)

// Ripple effect
const buttonRef = ref<ComponentPublicInstance | null>(null)
const rippleContainerRef = ref<HTMLElement | null>(null)

function createRipple(event: MouseEvent | KeyboardEvent) {
    if (props.loading) return
    const container = rippleContainerRef.value
    if (!container) return

    const button = buttonRef.value?.$el as HTMLElement | undefined
    if (!button) return
    const rect = button.getBoundingClientRect()

    let size = Math.max(rect.width, rect.height)
    let x
    let y

    if (iconOnly.value || event instanceof KeyboardEvent) {
        size *= 2
        x = (rect.width - size) / 2
        y = (rect.height - size) / 2
    } else {
        size *= 2
        x = event.clientX - rect.left - size / 2
        y = event.clientY - rect.top - size / 2
    }

    const ripple = document.createElement('span')
    ripple.style.width = ripple.style.height = `${size}px`
    ripple.style.left = `${x}px`
    ripple.style.top = `${y}px`
    ripple.className = 'ripple-wave ' + (size < 90 ? 'fast' : '')

    container.appendChild(ripple)
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true })
}

// Event handlers
function handlePointerDown(event: PointerEvent) {
    if (props.noActiveEffect || props.loading) return
    createRipple(event)
}

function handleKeyUp(event: KeyboardEvent) {
    if (props.loading) return

    if (event.key !== 'Enter' && event.key !== ' ') {
        return
    }

    if (!props.noActiveEffect) {
        createRipple(event)
    }

    emit('click', event)
}

function handleClick(event: MouseEvent) {
    if (props.loading) {
        // block native action (form submit / navigation) while busy
        event.preventDefault()
        return
    }
    emit('click', event)
}

// loading state with start-delay to prevent flickering on fast operations
const internalLoading = useDeferredRef(() => props.loading, { delayOn: 600, delayOff: 0 })

// Animate width changes when toggling between icon and label
watch([iconType, iconKey], async () => {
    const instance = buttonRef.value
    if (!instance) return
    const el = instance.$el as HTMLElement

    // Freeze at current visual width (handles mid-animation interrupts)
    el.classList.add('freeze-transition')
    const oldWidth = el.offsetWidth
    el.style.width = `${oldWidth}px`

    await nextTick()

    // Measure natural width of new content
    el.classList.add('is-resizing')
    el.style.width = 'auto'
    const newWidth = el.offsetWidth

    if (oldWidth === newWidth) {
        el.style.width = ''
        el.classList.remove('is-resizing', 'freeze-transition')
        return
    }

    // Reset to old width and animate to new
    el.style.width = `${oldWidth}px`
    void el.offsetWidth // force reflow
    el.classList.remove('freeze-transition')
    el.style.width = `${newWidth}px`

    el.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'width') {
            el.style.width = ''
            el.classList.remove('is-resizing')
        }
    }, { once: true })
}, { flush: 'pre' })
</script>

<template>
    <Primitive
        ref="buttonRef"
        data-slot="button"
        :as="props.as"
        :class="cn(
            buttonVariants({
                variant: props.variant,
                size: sizeProcessed,
                circle: iconOnly && props.circle
            }),
            props.class,
            {
                'min-w-32': !iconOnly,
                'icon-only': iconOnly,
                'has-icon': !iconOnly && iconKey,
                active
            }
        )"
        :type="props.type"
        :disabled="props.disabled"
        :aria-label="props.ariaLabel"
        :aria-pressed="props.active ? 'true' : 'false'"
        :aria-busy="props.loading ? 'true' : 'false'"
        :title="iconOnly ? props.title : undefined"
        @pointerdown="handlePointerDown"
        @keyup="handleKeyUp"
        @click="handleClick"
    >
        <transition-group
            name="smooth-list"
        >
            <span
                v-if="iconType"
                :key="iconType"
                class="item"
            >
                <SphIcon
                    v-if="internalLoading"
                    :icon="Loader"
                    :size="iconSize"
                    class="[&>svg]:animate-[spin_1.25s_linear_infinite]"
                />
                <transition
                    v-else
                    name="quick-switch"
                >
                    <SphIcon
                        v-if="props.icon"
                        :key="iconKey"
                        :icon="props.icon"
                        :size="iconSize"
                        :aria-label="iconOnly ? props.ariaLabel : false"
                    />
                </transition>
            </span>
            <span
                v-if="props.label || $slots.default"
                key="label"
                data-label
                class="item"
            >
                <slot>{{ props.label }}</slot>
            </span>
            <span
                v-if="iconRight && !props.loading"
                :key="iconKey + '-right'"
                class="item"
            >
                <SphIcon
                    :icon="iconRight"
                    :size="iconSize"
                />
            </span>
            <div
                ref="rippleContainerRef"
                key="ripple-container"
                class="ripple-container"
                aria-hidden="true"
            ></div>
        </transition-group>
    </Primitive>
</template>

<style scoped>
.quick-switch-enter-from {
    opacity: 0;
    transform: scale(1.2) translate(0, -10%);
}

.quick-switch-leave-to {
    opacity: 0;
    transform: scale(0.8) translate(0, 10%);
}

.quick-switch-enter-to,
.quick-switch-leave-from {
    opacity: 1;
    transform: scale(1) translate(0, 0);
}

.quick-switch-enter-active,
.quick-switch-leave-active {
    transition: opacity .4s, transform .4s;
}

.quick-switch-leave-active {
    position: absolute;
}

.item {
    display: inline-flex;
}

.smooth-list-enter-from {
    opacity: 0;
    transform: translate(50%, 0);

    &.right {
        transform: translate(-50%, 0);
    }
}

.smooth-list-leave-to {
    opacity: 0;
    transform: translate(-100%, 0);

    &.right {
        transform: translate(100%, 0);
    }
}

.smooth-list-enter-to,
.smooth-list-leave-from {
    opacity: 1;
    transform: translate(0, 0);
}

.smooth-list-enter-active,
.smooth-list-leave-active {
    transition: all .4s var(--ease-out-cubic);
}

.smooth-list-leave-active {
    position: absolute !important;
}

.smooth-list-move {
    transition: all .3s var(--ease-out-cubic);
}

.is-resizing {
    overflow: hidden;
    transition: width .3s var(--ease-out-cubic);
}

.freeze-transition {
    transition: none !important;
}

.ripple-container {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: inherit;
    pointer-events: none;
}
</style>

<style>
.ripple-wave {
    position: absolute;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.25;
    transform: scale(0);
    pointer-events: none;
    animation: ripple-expand .4s var(--ease-in-quad) forwards;

    &.fast {
        opacity: 0.5;
        animation-duration: .4s;
        animation-timing-function: var(--ease-in-quad);
    }
}

@keyframes ripple-expand {
    to {
        transform: scale(1);
        opacity: 0;
    }
}
</style>
